import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getUserAvatarSrc } from '../../../utils/avatarUtils';

const UserTable = ({
  users,
  currentUser,
  onToggleActivity,
  getSortIcon,
  onSort,
}) => {
  const { t } = useTranslation(['admin', 'common', 'auth']);

  const renderRoleChips = (user) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {Array.isArray(user.roles) ? user.roles.map((role) => (
          <Chip 
            key={role} 
            label={role} 
            size="small"
            color={role === 'admin' ? 'error' : role === 'teacher' ? 'warning' : 'default'}
          />
        )) : (
          <Typography variant="body2" color="text.secondary">
            {t('admin:users.no_roles')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ cursor: 'pointer' }}
              onClick={() => onSort('fullName')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {t('auth:username_label')}
                {getSortIcon('fullName')}
              </Box>
            </TableCell>
            <TableCell
              style={{ cursor: 'pointer' }}
              onClick={() => onSort('email')}
            >
              {t('auth:email_label')} {getSortIcon('email')}
            </TableCell>
            <TableCell>
              {t('admin:users.role')}
            </TableCell>
            <TableCell>
              {t('admin:users.status')}
            </TableCell>
            <TableCell
              style={{ cursor: 'pointer' }}
              onClick={() => onSort('createdAt')}
            >
              {t('admin:users.registration_date')}{' '}
              {getSortIcon('createdAt')}
            </TableCell>
            <TableCell>{t('admin:users.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={getUserAvatarSrc(user)}
                    alt={user.fullName}
                    sx={{ width: 40, height: 40 }}
                  />
                  <Typography>{user.fullName}</Typography>
                  {currentUser?.email === user.email && (
                    <Chip
                      label={t('common:you')}
                      size="small"
                      color="primary"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: 'medium',
                      }}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {renderRoleChips(user)}
                </Box>
              </TableCell>
              <TableCell>
                {!user.isActive ? (
                  <Chip
                    label={t('admin:users.status_banned')}
                    color="error"
                  />
                ) : (
                  <Chip
                    label={t('admin:users.status_active')}
                    color="success"
                  />
                )}
              </TableCell>
              <TableCell>
                {new Date(user.metadata.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip
                    title={
                      user.isActive
                        ? t('admin:users.deactivate')
                        : t('admin:users.activate')
                    }
                  >
                    <IconButton
                      onClick={() =>
                        onToggleActivity(user.id, user.isActive)
                      }
                      color={!user.isActive ? 'error' : 'success'}
                      size="small"
                    >
                      {!user.isActive ? (
                        <ToggleOff fontSize="small" />
                      ) : (
                        <ToggleOn fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserTable; 